import IconButton from 'components/@extended/IconButton';
import StatusChip from 'components/@extended/StatusChip';
import { draftPageOf } from 'components/create-dialog/recordTypes';
import {
    deletesSoon,
    DeletesOnLine,
    draftTypeName,
    retentionDaysOf,
    SavedLine,
    ShareLine,
    useDeleteSavedDraft,
} from 'components/create-dialog/SavedForLaterList';
import DrawerPortal from 'components/drawer/DrawerPortal';
import { AccessTimeOutlined, Close as CloseIcon } from 'components/icons';
import useAuth from 'hooks/useAuth';
import { useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { dispatch, useSelector } from 'store';
import { useGetDraftsQuery } from 'store/api/drafts';
import { closeTemporaryDrawer, setNoBackdrop, toggleTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { DraftDetail } from 'types/drafts';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import StyledHeaderButton, { HeaderButtonCount } from './StyledHeaderButton';

const DRAWER_ID = 'saved-for-later';

/** The panel's small grey buttons. */
const ACTION_CLASS =
    'border-divider bg-grey-50 hover:bg-grey-100 text-meta flex h-7 cursor-pointer items-center rounded-md border px-2.5 font-semibold no-underline';

const closeDrawer = () => dispatch(closeTemporaryDrawer());

type PanelProps = { drafts: DraftDetail[]; titleId: string };

/** The classic design's Saved for later: the drafts the user sees in the header's panel, latest save first. */
function SavedForLaterPanel({ drafts, titleId }: PanelProps) {
    const { user } = useAuth();
    const remove = useDeleteSavedDraft(drafts.length, closeDrawer);
    const retentionDays = retentionDaysOf(drafts);

    return (
        <div className="flex h-full flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-6 pt-7 pb-3">
                <h2 id={titleId} className="flex-1 text-base font-semibold">
                    <FormattedMessage id="create-dialog.saved.title" />
                </h2>
                <IconButton aria-label={t('close')} className="text-grey-600 hover:text-fg" onClick={closeDrawer}>
                    <CloseIcon />
                </IconButton>
            </div>
            <div className="border-grey-200 bg-grey-50 border-y px-6 py-2.5 font-semibold">
                <FormattedMessage id="create-dialog.saved.count" values={{ count: drafts.length }} />
            </div>
            <ul className="min-h-0 flex-1 overflow-y-auto">
                {drafts.map((entry) => {
                    const page = draftPageOf(entry.type, entry.id);
                    const typeName = draftTypeName(entry.type);
                    return (
                        <li
                            key={`${entry.type}/${entry.id}`}
                            className="border-grey-200 flex flex-col items-start gap-1 border-b px-6 py-4"
                        >
                            <span>
                                {entry.name ? <span className="text-grey-600">{typeName} · </span> : null}
                                <Link to={page} onClick={closeDrawer} className="text-link font-medium no-underline">
                                    {entry.name ?? typeName}
                                </Link>
                            </span>
                            <span className="text-meta text-grey-600">
                                <SavedLine entry={entry} />
                            </span>
                            <span className="text-meta text-grey-600">
                                <ShareLine entry={entry} userId={user?.id} />
                            </span>
                            {deletesSoon(entry) ? (
                                <StatusChip
                                    type="warning"
                                    className="text-warning-darker"
                                    label={<DeletesOnLine entry={entry} />}
                                />
                            ) : null}
                            <div className="mt-1.5 flex gap-2">
                                <Link to={page} onClick={closeDrawer} className={cn(ACTION_CLASS, 'text-fg')}>
                                    <FormattedMessage id="create-dialog.saved.open" />
                                </Link>
                                {entry.right === 'OWNER' ? (
                                    <button
                                        type="button"
                                        className={cn(ACTION_CLASS, 'text-error-darker')}
                                        aria-label={`${t('delete-draft')} ${entry.name ?? typeName}`}
                                        onClick={() => remove(entry)}
                                    >
                                        <FormattedMessage id="delete" />
                                    </button>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {retentionDays === undefined ? null : (
                <p className="text-meta text-grey-500 px-6 py-3">
                    <FormattedMessage id="create-dialog.saved.note" values={{ days: retentionDays }} />
                </p>
            )}
        </div>
    );
}

/**
 * The header's Saved for later pill, left of Tasks, and the panel it opens. It counts every draft the user sees and
 * is not there while there are none, or while drafts are off.
 */
export default function SavedForLater() {
    const titleId = useId();
    const isEnabled = useIsFeatureEnabled(FeatureFlag.SAVE_AS_DRAFT);
    const { data: drafts = [] } = useGetDraftsQuery(undefined, { skip: !isEnabled });
    const isOpen = useSelector((state) => state.temporaryDrawer.activeDrawerId === DRAWER_ID);

    const toggleDrawer = () => {
        if (isOpen) {
            closeDrawer();
        } else {
            dispatch(toggleTemporaryDrawer(DRAWER_ID));
            dispatch(setNoBackdrop(true));
        }
    };

    return (
        <>
            {drafts.length > 0 ? (
                <StyledHeaderButton
                    aria-label={`${t('create-dialog.saved.title')}, ${t('create-dialog.saved.count', { count: String(drafts.length) })}`}
                    aria-expanded={isOpen}
                    className={cn(isOpen && 'bg-grey-300')}
                    onClick={toggleDrawer}
                >
                    <AccessTimeOutlined className="text-[16px]" />
                    <HeaderButtonCount count={drafts.length} />
                </StyledHeaderButton>
            ) : null}
            {/* Mounted with no drafts too: a panel still open when the last draft goes elsewhere keeps its Close. */}
            <DrawerPortal
                content={<SavedForLaterPanel drafts={drafts} titleId={titleId} />}
                drawerId={DRAWER_ID}
                type="temporary"
                width={480}
                widensBelowSm
                labelledBy={titleId}
            />
        </>
    );
}
